import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Stack,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { supabase } from '../services/supabase';
import { Employee } from '../types';

interface EmployeeRegistrationProps {
  onRegistrationComplete?: (employee: Employee) => void;
}

interface RegistrationForm {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  phone: string;
  address: string;
  position: string;
  department: string;
  employment_type: 'regular' | 'contractual' | 'part-time';
  gender: 'male' | 'female';
  salary: string;
  hourly_rate: string;
  bank_account: string;
  sss_number: string;
  philhealth_number: string;
  pagibig_number: string;
  tin_number: string;
  deduct_sss: boolean;
  deduct_philhealth: boolean;
  deduct_pagibig: boolean;
  deduct_tax: boolean;
}

const EmployeeRegistration: React.FC<EmployeeRegistrationProps> = ({ onRegistrationComplete }) => {
  const [formData, setFormData] = useState<RegistrationForm>({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    position: '',
    department: '',
    employment_type: 'regular',
    gender: 'male',
    salary: '',
    hourly_rate: '',
    bank_account: '',
    sss_number: '',
    philhealth_number: '',
    pagibig_number: '',
    tin_number: '',
    deduct_sss: true,
    deduct_philhealth: true,
    deduct_pagibig: true,
    deduct_tax: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = (): string | null => {
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      return 'Please fill in all required fields';
    }

    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }

    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters long';
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }

    if (formData.email.length > 254) {
      return 'Email address is too long';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        throw new Error(`Authentication error: ${authError.message}`);
      }

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      // Step 2: Create employee record
      const employeeData: Omit<Employee, 'id' | 'created_at' | 'updated_at'> = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        position: formData.position,
        department: formData.department,
        hire_date: new Date().toISOString().split('T')[0],
        salary: parseFloat(formData.salary) || 0,
        hourly_rate: parseFloat(formData.hourly_rate) || undefined,
        employment_type: formData.employment_type,
        gender: formData.gender,
        bank_account: formData.bank_account,
        sss_number: formData.sss_number || undefined,
        philhealth_number: formData.philhealth_number || undefined,
        pagibig_number: formData.pagibig_number || undefined,
        tin_number: formData.tin_number || undefined,
        deduct_sss: formData.deduct_sss,
        deduct_philhealth: formData.deduct_philhealth,
        deduct_pagibig: formData.deduct_pagibig,
        deduct_tax: formData.deduct_tax,
        status: 'active',
      };

      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .insert([employeeData])
        .select()
        .single();

      if (employeeError) {
        throw new Error(`Employee creation error: ${employeeError.message}`);
      }

      // Step 3: Create public.users record to link auth.user with employee
      const { error: userError } = await supabase
        .from('users')
        .insert([
          {
            id: authData.user.id,
            email: formData.email,
            role: 'employee',
            employee_id: employee.id,
          },
        ]);

      if (userError) {
        throw new Error(`User linking error: ${userError.message}`);
      }

      // Step 4: Confirm email automatically (for development)
      const { error: confirmError } = await supabase.auth.admin.updateUserById(
        authData.user.id,
        { email_confirm: true }
      );

      if (confirmError) {
        console.warn('Email confirmation error:', confirmError.message);
        // Don't throw here as the main registration is complete
      }

      setSuccess('Employee registration completed successfully! You can now log in.');
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone: '',
        address: '',
        position: '',
        department: '',
        employment_type: 'regular',
        gender: 'male',
        salary: '',
        hourly_rate: '',
        bank_account: '',
        sss_number: '',
        philhealth_number: '',
        pagibig_number: '',
        tin_number: '',
        deduct_sss: true,
        deduct_philhealth: true,
        deduct_pagibig: true,
        deduct_tax: true,
      });

      if (onRegistrationComplete) {
        onRegistrationComplete(employee);
      }

    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <CardHeader
        title="Employee Registration"
        subheader="Create a new employee account"
        avatar={<PersonAddIcon />}
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Account Information */}
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              sx={{ maxWidth: { md: '50%' } }}
            />

            {/* Personal Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Personal Information
            </Typography>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
              />
            </Stack>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Stack>

            {/* Employment Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Employment Information
            </Typography>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                required
              />
            </Stack>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  name="employment_type"
                  value={formData.employment_type}
                  onChange={handleSelectChange}
                  label="Employment Type"
                >
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="contractual">Contractual</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleSelectChange}
                  label="Gender"
                  required
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            
            <TextField
              fullWidth
              label="Monthly Salary"
              name="salary"
              type="number"
              value={formData.salary}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: '₱',
              }}
              sx={{ maxWidth: { md: '50%' } }}
            />
            
            <TextField
              fullWidth
              label="Hourly Rate (Optional)"
              name="hourly_rate"
              type="number"
              value={formData.hourly_rate}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: '₱',
              }}
              sx={{ maxWidth: { md: '50%' } }}
            />

            {/* Financial Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Financial Information
            </Typography>
            
            <TextField
              fullWidth
              label="Bank Account Number"
              name="bank_account"
              value={formData.bank_account}
              onChange={handleInputChange}
              sx={{ maxWidth: { md: '50%' } }}
            />

            {/* Government IDs */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Government IDs (Optional)
            </Typography>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="SSS Number"
                name="sss_number"
                value={formData.sss_number}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="PhilHealth Number"
                name="philhealth_number"
                value={formData.philhealth_number}
                onChange={handleInputChange}
              />
            </Stack>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Pag-IBIG Number"
                name="pagibig_number"
                value={formData.pagibig_number}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="TIN Number"
                name="tin_number"
                value={formData.tin_number}
                onChange={handleInputChange}
              />
            </Stack>

            {/* Payroll Deduction Preferences */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Payroll Deduction Preferences
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="deduct_sss"
                    checked={formData.deduct_sss}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deduct_sss: e.target.checked
                    }))}
                  />
                }
                label="Deduct SSS Contribution"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="deduct_philhealth"
                    checked={formData.deduct_philhealth}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deduct_philhealth: e.target.checked
                    }))}
                  />
                }
                label="Deduct PhilHealth Contribution"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="deduct_pagibig"
                    checked={formData.deduct_pagibig}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deduct_pagibig: e.target.checked
                    }))}
                  />
                }
                label="Deduct Pag-IBIG Contribution"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="deduct_tax"
                    checked={formData.deduct_tax}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deduct_tax: e.target.checked
                    }))}
                  />
                }
                label="Deduct Withholding Tax"
              />
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Registering...' : 'Register Employee'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmployeeRegistration;